import { useRBAC } from '@/lib/rbac/context'
import { ATTRIBUTES, PERMISSIONS } from '@/lib/rbac/constants'

/**
 * Hook to control access to salary information
 * Uses RBAC attribute-based permissions to determine access
 */
export function useSalaryAccess() {
  const { hasPermission, isLoading } = useRBAC()

  // Check permissions using RBAC
  const canViewSalaries = !isLoading && hasPermission('attribute', ATTRIBUTES.EMPLOYEE_SALARY, PERMISSIONS.ATTRIBUTE_VIEW)
  const canEditSalaries = !isLoading && hasPermission('attribute', ATTRIBUTES.EMPLOYEE_SALARY, PERMISSIONS.ATTRIBUTE_EDIT)
  const canViewPayroll = !isLoading && hasPermission('attribute', ATTRIBUTES.PAYROLL_DATA, PERMISSIONS.ATTRIBUTE_VIEW)

  /**
   * Formats monetary values, hiding them if the user doesn't have permission
   * @param value - The salary value to format
   * @param fallbackText - Text to display when user doesn't have permission (default: "Hidden")
   */
  const formatSalaryValue = (value: number | null | undefined, fallbackText = 'Hidden') => {
    if (!canViewSalaries || value === null || value === undefined) {
      return fallbackText
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  /**
   * Returns the salary value or a fallback value if permission is denied
   * @param value - The salary value
   * @param fallbackValue - Value to return when user doesn't have permission (default: 0)
   */
  const getSalaryValue = (value: number | null | undefined, fallbackValue = 0) => {
    return canViewSalaries && value !== null && value !== undefined ? value : fallbackValue
  }

  return {
    canViewSalaries,
    canEditSalaries,
    canViewPayroll,
    formatSalaryValue,
    getSalaryValue,
    isLoading,
  }
}